<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";

  .input-compact {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 570px;

    .dropdown-row {
      display: flex;
      align-items: center;
      background-color: colors.get(interfaceSwGrey_10);
      border-radius: utils.in_rem(10px);
      padding: utils.in_rem(2px);
    }

    .label {
      width: 240px;
      @include mixins.body2();
      padding: 0 utils.in_rem(14px);
    }

    color: colors.get(textBlack);
    @include mixins.body2();

    .lock-button {
      background: none;
      border: none;
      cursor: pointer;
      color: colors.get(textGrey);

      &:hover {
        color: colors.get(textBlack);
      }
    }
  }
</style>

<script>
  import IconScale from "@sourcewhale/shared/svelte-design-system/components/atoms/IconScale.svelte";
  import Tooltip from "@sourcewhale/shared/svelte-design-system/components/atoms/Tooltip.svelte";
  import SingleSelectDropdown from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/SingleSelectDropdown/SingleSelectDropdown.svelte";
  import { activeProviderModalStore, integrationStore } from "../../stores";
  import InfoIcon from "./InfoIcon.svelte";

  export let type;
  export let setting;

  $: provider = $activeProviderModalStore.title;

  // Special case for syncWhen - remap labels
  $: isSyncWhen = setting.key === "syncWhen";
  $: displayTitle = isSyncWhen
    ? `Add ${type === "ClientContact" ? "Contact" : type} to ${provider}`
    : setting.title;

  // Transform values to match the components' expected format
  $: items =
    setting.values
      ?.filter(
        (item) =>
          !type ||
          !item.syncAsRestrictions ||
          item.syncAsRestrictions.includes(
            type.charAt(0).toUpperCase() + type.slice(1)
          )
      )
      ?.map((item) => {
        // Special case for syncWhen - remap labels
        if (isSyncWhen) {
          const remappedLabels = {
            "Don't sync": "don't add",
            "Sync upon adding": "when added to a campaign",
            "Sync upon interested": "only when they're interested",
            "Sync upon reply": "only when they reply",
          };
          return {
            id: item.val,
            value: item.val,
            label: remappedLabels[item.key] || item.key,
          };
        }
        return {
          id: item.val,
          value: item.val,
          label: item.key,
        };
      }) || [];

  // Get the current value from the store
  $: storeValue = $integrationStore.getValue(setting.key, type);

  $: selected = storeValue;

  $: isLocked =
    setting.lockable && $integrationStore.getLockedChoice(setting.key, type);

  // Watch for changes to selected and update the store
  $: if (selected !== storeValue && !isLocked) {
    $integrationStore.setValue(setting.key, selected, type);
  }

  const handleLockToggle = () => {
    if (setting.lockable)
      $integrationStore.setLockedChoice(setting.key, !isLocked, type);
  };
</script>

<div class="input-compact">
  <div class="dropdown-row">
    <div class="label">
      <span>{displayTitle}</span>
      {#if setting.description}
        <Tooltip text={setting.description} />
      {/if}
    </div>
    <SingleSelectDropdown
      style="width:300px; height: 2.5rem;"
      size="large"
      {items}
      bind:selected
      disabled={isLocked}
      placeholder="Select one"
      showFilter={items.length > 10}
      restrictHeight
    />
  </div>
  {#if setting.lockable}
    <button
      class="lock-button"
      on:click={handleLockToggle}
      aria-label={isLocked ? "Unlock setting" : "Lock setting"}
    >
      <IconScale
        class={isLocked ? "fa-solid fa-lock" : "fa-regular fa-lock-open"}
        size="medium"
      >
        <span slot="tooltip">
          {#if isLocked}
            {setting.lockedMessage || "This setting is locked"}
          {:else}
            Click to lock this setting
          {/if}
        </span>
      </IconScale>
    </button>
  {/if}
  {#if setting.info}
    <InfoIcon>{setting.info}</InfoIcon>
  {/if}
</div>
